import styled from "styled-components";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "hooks/hook_util/debounce/useDebounce";
import {
  useCharacterSearch,
  useCharacterSearchRefetch,
} from "hooks/asocijator/character/useCharacterSearch";
import { SEARCH_NAMES } from "constant/search/search";
import { useInvalidateQueryByKeys } from "hooks/query_common/useInvalidateQueryByKeys";
import { QUERY_KEYS } from "constant/react_query";
import { CharacterResult } from "common/character/dto/CharacterResult";
import {
  searchSelectInitial,
  searchSelectResults,
  showSearchSelect,
} from "./CharacterSearchSelect.logic";
import CharacterSelection from "./CharacterSelection";
import { ReactRefI } from "type/common/ref.type";
import { CharacterSearchParams } from "common/api/asocijator/character/type/character_api.type";
import { ItemTag } from "common/tag/dto/ItemTag";
import { getSignImagePath } from "common/character/character_common";
import CharacterImage from "../../file/CharacterImage";

type CharacterSearchSelectProps = {
  excludeIds?: string[];
  excludeLetters?: string[];
  onValueSelect?: Function;
  onLetterSelect?: Function;
  label?: string;
  characterSelection?: CharacterResult;
  characterSelectionDeleteHandle?: Function;
  topicId?: string;
  tags?: ItemTag[];
};

const getOptionLabel = (option: CharacterResult) =>
  `${option.letter} ${option.pronounce} ${option.meaning}`;

const renderOption = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: CharacterResult
) => {
  const signImage: string | null = getSignImagePath(option);

  return (
    <li {...props}>
      {signImage && (
        <CharacterImage characterFile={{ path: signImage }} small />
      )}
      {!signImage && option.letter}
      {option.pronounce} {option.meaning}
    </li>
  );
};

const CharacterSearchSelect = ({
  excludeIds,
  excludeLetters,
  onValueSelect,
  onLetterSelect,
  label,
  characterSelection,
  characterSelectionDeleteHandle,
  topicId,
  tags,
}: CharacterSearchSelectProps) => {
  const [value, setValue] = useState<CharacterResult | null>(null);
  const [searchFocus, setSearchFocus] = useState<boolean>(false);
  const [searchFocusInitial, setSearchFocusInitial] = useState<boolean>(false);
  const searchOptionsTimeRef: ReactRefI<null | number> = useRef<null | number>(
    null
  );
  const [searchParams, setSearchParams] = useState<CharacterSearchParams>(
    searchSelectInitial(topicId, tags)
  );
  const { data: characterListResult, refetch } =
    useCharacterSearchRefetch(searchParams);
  const handleCharacterSearch = useDebounce((searchText: string) => {
    setSearchParams({
      ...searchParams,
      [SEARCH_NAMES.SEARCH_TEXT]: searchText,
      skip: 0,
    });
  }, 500);
  const invalidateQuery = useInvalidateQueryByKeys();
  useEffect(() => {
    const currentTime = new Date().getTime();

    // if (
    //   searchOptionsTimeRef.current === null ||
    //   searchOptionsTimeRef.current < currentTime
    // ) {
    //   invalidateQuery.invalidateQueriesByKeys([
    //     QUERY_KEYS.ASOCIJATOR_CHARACTER_SEARCH,
    //   ]);
    //   searchOptionsTimeRef.current = currentTime;
    // }

    if (searchFocusInitial) {
      refetch();
    }
  }, [searchParams, invalidateQuery]);

  const results: CharacterResult[] = useMemo(() => {
    return characterListResult
      ? searchSelectResults(
          characterListResult["items"],
          excludeIds,
          excludeLetters
        )
      : [];
  }, [characterListResult, excludeIds, excludeLetters]);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchFocus && inputRef.current) {
      inputRef.current.focus();
      setSearchFocus(false);
    }
  }, [searchFocus]);

  const changeHandle = (event: any, newValue: CharacterResult | null) => {
    if (onValueSelect) {
      onValueSelect(newValue);
      setValue(null);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
    if (onLetterSelect && newValue) {
      onLetterSelect(newValue.letter);
      setValue(null);
      if (inputRef.current) {
        inputRef.current.focus();
      }
      setSearchFocus(true);
    }
  };

  const textChangeHandle = (event: any) => {
    handleCharacterSearch(event.target.value);
  };

  const focusHandle = (event: any) => {
    if (!searchFocusInitial) {
      setSearchFocusInitial(true);
    }
    console.log("focusHandle");
  };

  return (
    <DivNav>
      {characterSelection &&
        characterSelectionDeleteHandle &&
        showSearchSelect(characterSelection) && (
          <DivSelection>
            <CharacterSelection
              characterSelection={characterSelection}
              characterSelectionDeleteHandle={characterSelectionDeleteHandle}
            />
          </DivSelection>
        )}
      <Autocomplete
        getOptionLabel={getOptionLabel}
        value={value}
        id="combo-box-demo"
        options={results}
        renderOption={renderOption}
        blurOnSelect
        sx={{ width: 300 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label ?? "Character"}
            onChange={textChangeHandle}
            inputRef={inputRef}
            onFocus={focusHandle}
          />
        )}
        onChange={changeHandle}
      />
    </DivNav>
  );
};

const DivNav = styled.div`
  display: flex;
  margin-top: 0rem;
`;

const DivSelection = styled.div`
  margin-right: 1rem;
`;

export default CharacterSearchSelect;
